import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useSupabaseAuth'
import { useSubscription } from '../hooks/useSubscription'
import { Check, X, CreditCard, Clock, Users, Star, Zap, Crown, Gem, Info } from 'lucide-react'
import { componentClasses, cn } from '../utils/designSystem'

const SubscriptionPackages = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { packages, loading, createTransaction } = useSubscription()
  const [processingPackage, setProcessingPackage] = useState(null)

  const handleSubscribe = async (packageData) => {
    if (!user) {
      // Redirect to login if not authenticated
      navigate('/login')
      return
    }

    try {
      setProcessingPackage(packageData.id)
      
      const { data: transaction, error } = await createTransaction(packageData.id)
      
      if (error) {
        console.error('Error creating transaction:', error)
        alert('Terjadi kesalahan saat membuat transaksi. Silakan coba lagi.')
        return
      }

      // Navigate to payment confirmation page
      navigate(`/payment-confirmation/${transaction.order_id}`)
    } catch (error) {
      console.error('Error:', error)
      alert('Terjadi kesalahan. Silakan coba lagi.')
    } finally {
      setProcessingPackage(null)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(price)
  }

  const getPackageFeatures = (packageName, features) => {
    const allowedAgents = features?.allowed_agents || []
    
    const featureList = [
      { name: 'Search Agent', included: true, cost: '5 kredit/penggunaan' },
      { name: 'Writing Agent', included: true, cost: '1-2 kredit/penggunaan' },
      { name: 'Trending Agent', included: true, cost: '2 kredit/penggunaan' },
      { name: 'Artikel Agent', included: true, cost: '7 kredit/penggunaan' },
      { name: 'Berita Agent', included: true, cost: '7 kredit/penggunaan' },
      { name: 'PPT Agent', included: true, cost: '20 kredit/penggunaan' },
      { name: 'Chart Agent', included: true, cost: '10 kredit/penggunaan' },
      { name: 'PDF Agent', included: true, cost: '10 kredit/penggunaan' },
      { name: 'Makalah Agent', included: true, cost: '30 kredit/penggunaan' },
      { name: 'Gambar Agent', included: true, cost: '5 kredit/penggunaan' },
      { name: 'Research Agent', included: true, cost: '30 kredit/penggunaan' },
      { name: 'Video Agent', included: ['LARGE', 'PRO'].includes(packageName), cost: '30 kredit/penggunaan' },
      { name: 'Skripsi Agent', included: ['LARGE', 'PRO'].includes(packageName), cost: '100 kredit/penggunaan' },
      { name: 'Buku Agent', included: packageName === 'PRO', cost: '200 kredit/penggunaan' },
      { name: 'Novel Agent', included: packageName === 'PRO', cost: '200 kredit/penggunaan' }
    ]

    return featureList
  }

  const getPackageColor = (packageName) => {
    switch (packageName) {
      case 'STARTER':
        return {
          accent: 'bg-text-tertiary',
          border: 'border-border-secondary',
          ring: 'ring-text-tertiary/20'
        }
      case 'MEDIUM':
        return {
          accent: 'bg-brand-primary',
          border: 'border-brand-primary/30',
          ring: 'ring-brand-primary/20'
        }
      case 'LARGE':
        return {
          accent: 'bg-text-secondary',
          border: 'border-text-secondary/30',
          ring: 'ring-text-secondary/20'
        }
      case 'PRO':
        return {
          accent: 'bg-gradient-to-r from-purple-500 to-pink-500',
          border: 'border-purple-500/30',
          ring: 'ring-purple-500/20'
        }
      default:
        return {
          accent: 'bg-text-tertiary',
          border: 'border-border-secondary',
          ring: 'ring-text-tertiary/20'
        }
    }
  }

  const getPackageIcon = (packageName) => {
    switch (packageName) {
      case 'STARTER':
        return <Zap className="w-6 h-6" />
      case 'MEDIUM':
        return <Star className="w-6 h-6" />
      case 'LARGE':
        return <Crown className="w-6 h-6" />
      case 'PRO':
        return <Gem className="w-6 h-6" />
      default:
        return <CreditCard className="w-6 h-6" />
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    )
  }

  return (
    <div className={cn(componentClasses.main, "min-h-screen")}>
      <div className="max-w-6xl mx-auto px-1 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-12">
        <div className="text-center mb-12 px-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            Pilih Paket Berlangganan
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base md:text-lg px-2">
            Dapatkan akses ke berbagai fitur AI dengan paket yang sesuai kebutuhan Anda.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 px-1 sm:px-2 md:px-0">
          {packages.filter(pkg => pkg.name !== 'FREE').map((pkg) => {
            const features = getPackageFeatures(pkg.name, pkg.features)
            const isProcessing = processingPackage === pkg.id
            const isPopular = pkg.name === 'MEDIUM'
            const isPremium = pkg.name === 'PRO'
            
            return (
              <div
                key={pkg.id}
                className={cn(
                  "relative bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl transition-all duration-300 hover:border-gray-700 hover:bg-gray-900/70 group",
                  isPopular && "border-blue-500/50 bg-blue-950/20",
                  isPremium && "border-purple-500/50 bg-purple-950/20"
                )}
              >
                {isPopular && (
                  <div className="absolute -top-2 left-4">
                    <div className="px-2 py-1 bg-blue-500 text-white text-xs font-medium rounded-md">
                      Populer
                    </div>
                  </div>
                )}
                {isPremium && (
                  <div className="absolute -top-2 left-4">
                    <div className="px-2 py-1 bg-purple-500 text-white text-xs font-medium rounded-md">
                      Premium
                    </div>
                  </div>
                )}
                
                <div className="p-4 sm:p-6">
                  {/* Header */}
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        isPopular ? "bg-blue-500/20 text-blue-400" :
                        isPremium ? "bg-purple-500/20 text-purple-400" :
                        "bg-gray-700/50 text-gray-400"
                      )}>
                        {getPackageIcon(pkg.name)}
                      </div>
                      <h3 className="text-xl font-semibold text-white">{pkg.name}</h3>
                    </div>
                    
                    <div className="mb-4">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-white">{formatPrice(pkg.price)}</span>
                        <span className="text-sm text-gray-400">/30 hari</span>
                      </div>
                    </div>
                    
                    <div className={cn(
                      "inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium",
                      isPopular ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" :
                      isPremium ? "bg-purple-500/10 text-purple-400 border border-purple-500/20" :
                      "bg-gray-700/30 text-gray-300 border border-gray-700"
                    )}>
                      {pkg.credits.toLocaleString('id-ID')} Kredit
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-2 mb-8">
                    {features.slice(0, 6).map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className={cn(
                          "w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0",
                          feature.included ? "bg-green-500/20" : "bg-gray-600/30"
                        )}>
                          {feature.included ? (
                            <Check className="w-2.5 h-2.5 text-green-400" />
                          ) : (
                            <X className="w-2.5 h-2.5 text-gray-500" />
                          )}
                        </div>
                        <span className={cn(
                          "text-sm",
                          feature.included ? "text-gray-200" : "text-gray-500"
                        )}>
                          {feature.name}
                        </span>
                      </div>
                    ))}
                    
                    {features.length > 6 && (
                      <div className="pt-2">
                        <span className="text-xs text-gray-400">
                          +{features.length - 6} fitur lainnya
                        </span>
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSubscribe(pkg)}
                    disabled={isProcessing}
                    className={cn(
                      "w-full py-3 px-4 rounded-xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed",
                      isProcessing
                        ? 'bg-gray-800 text-gray-400 cursor-not-allowed'
                        : isPopular
                        ? 'bg-blue-500 hover:bg-blue-600 text-white'
                        : isPremium
                        ? 'bg-purple-500 hover:bg-purple-600 text-white'
                        : 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600'
                    )}
                  >
                    {isProcessing ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                        <span>Memproses...</span>
                      </div>
                    ) : (
                      'Pilih Paket'
                    )}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        {/* Feature Comparison Table */}
        <div className="mt-12 mb-8 mx-0">
          <div className="text-center mb-8 px-2">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 sm:mb-3">Perbandingan Fitur Paket</h3>
            <p className="text-xs sm:text-sm md:text-base text-gray-400 px-2">Lihat perbedaan fitur yang didapatkan di setiap paket</p>
          </div>
          
          <div className="mx-2 sm:mx-4 md:mx-0 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
              <table className="w-full min-w-[320px] sm:min-w-[500px]">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left p-1 sm:p-2 md:p-4 lg:p-6 text-white font-semibold text-xs sm:text-sm md:text-base w-1/3">Fitur</th>
                    {packages.filter(pkg => pkg.name !== 'FREE').map((pkg) => (
                      <th key={pkg.id} className="text-center p-1 sm:p-2 md:p-4 lg:p-6 text-white font-semibold min-w-[60px] sm:min-w-[80px] md:min-w-[100px] w-1/6">
                        <div className="flex flex-col items-center gap-1 sm:gap-2">
                          <div className={cn(
                            "w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center",
                            pkg.name === 'MEDIUM' ? "bg-blue-500/20 text-blue-400" :
                            pkg.name === 'PRO' ? "bg-purple-500/20 text-purple-400" :
                            "bg-gray-700/50 text-gray-400"
                          )}>
                            {getPackageIcon(pkg.name)}
                          </div>
                          <span className="text-xs sm:text-sm">{pkg.name}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'Search Agent', credit: '5 kredit', starter: true, medium: true, large: true, pro: true, highlight: false },
                    { name: 'Writing Agent', credit: '1-2 kredit', starter: true, medium: true, large: true, pro: true, highlight: false },
                    { name: 'Trending Agent', credit: '2 kredit', starter: true, medium: true, large: true, pro: true, highlight: false },
                    { name: 'Artikel Agent', credit: '7 kredit', starter: true, medium: true, large: true, pro: true, highlight: false },
                    { name: 'Berita Agent', credit: '7 kredit', starter: true, medium: true, large: true, pro: true, highlight: false },
                    { name: 'PPT Agent', credit: '20 kredit', starter: true, medium: true, large: true, pro: true, highlight: false },
                    { name: 'Chart Agent', credit: '10 kredit', starter: true, medium: true, large: true, pro: true, highlight: false },
                    { name: 'PDF Agent', credit: '10 kredit', starter: true, medium: true, large: true, pro: true, highlight: false },
                    { name: 'Makalah Agent', credit: '30 kredit', starter: true, medium: true, large: true, pro: true, highlight: false },
                    { name: 'Gambar Agent', credit: '5 kredit', starter: true, medium: true, large: true, pro: true, highlight: false },
                    { name: 'Research Agent', credit: '30 kredit', starter: true, medium: true, large: true, pro: true, highlight: false },
                    { name: 'Video Agent', credit: '30 kredit', starter: false, medium: false, large: true, pro: true, highlight: true },
                    { name: 'Skripsi Agent', credit: '100 kredit', starter: false, medium: false, large: true, pro: true, highlight: true },
                    { name: 'Buku Agent', credit: '200 kredit', starter: false, medium: false, large: false, pro: true, highlight: true },
                    { name: 'Novel Agent', credit: '200 kredit', starter: false, medium: false, large: false, pro: true, highlight: true }
                  ].map((feature, index) => (
                    <tr key={index} className={cn(
                      "border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors",
                      feature.highlight && "bg-gradient-to-r from-blue-950/20 to-purple-950/20"
                    )}>
                      <td className="p-1 sm:p-2 md:p-4 lg:p-6">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <span className={cn(
                                "text-[10px] sm:text-xs md:text-sm font-medium",
                                feature.highlight ? "text-white" : "text-gray-200"
                              )}>
                                {feature.name}
                              </span>
                              {feature.highlight && (
                                <span className="px-1 py-0.5 sm:px-2 sm:py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-[8px] sm:text-[9px] md:text-xs font-medium rounded-full">
                                  Premium
                                </span>
                              )}
                            </div>
                            <span className="text-[8px] sm:text-[9px] md:text-xs text-gray-400 mt-0.5">
                              {feature.credit}/penggunaan
                            </span>
                          </div>
                        </div>
                      </td>
                      {packages.filter(pkg => pkg.name !== 'FREE').map((pkg) => {
                        const hasFeature = (
                          (pkg.name === 'STARTER' && feature.starter) ||
                          (pkg.name === 'MEDIUM' && feature.medium) ||
                          (pkg.name === 'LARGE' && feature.large) ||
                          (pkg.name === 'PRO' && feature.pro)
                        )
                        
                        return (
                          <td key={pkg.id} className="p-1 sm:p-2 md:p-4 lg:p-6 text-center">
                            <div className="flex justify-center">
                              {hasFeature ? (
                                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                                  <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                                </div>
                              ) : (
                                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gray-600/30 flex items-center justify-center">
                                  <X className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                                </div>
                              )}
                            </div>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Upgrade CTA */}
            <div className="p-3 sm:p-6 bg-gradient-to-r from-blue-950/30 to-purple-950/30 border-t border-gray-800">
              <div className="text-center">
                <h4 className="text-sm sm:text-lg font-semibold text-white mb-2">Butuh Fitur Lebih Lengkap?</h4>
                <p className="text-gray-300 text-xs sm:text-sm mb-2 sm:mb-4 px-1">
                  Upgrade ke paket LARGE atau PRO untuk mendapatkan akses ke Video Agent, Skripsi Agent, Buku Agent, dan Novel Agent
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-1 sm:gap-3">
                  <div className="flex items-center gap-1 px-1.5 sm:px-3 py-1 sm:py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-500/20 rounded flex items-center justify-center">
                      <Check className="w-2 h-2 sm:w-3 sm:h-3 text-blue-400" />
                    </div>
                    <span className="text-[10px] sm:text-sm text-blue-400">Video Agent (LARGE & PRO)</span>
                  </div>
                  <div className="flex items-center gap-1 px-1.5 sm:px-3 py-1 sm:py-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-500/20 rounded flex items-center justify-center">
                      <Check className="w-2 h-2 sm:w-3 sm:h-3 text-purple-400" />
                    </div>
                    <span className="text-[10px] sm:text-sm text-purple-400">Skripsi Agent (LARGE & PRO)</span>
                  </div>
                  <div className="flex items-center gap-1 px-1.5 sm:px-3 py-1 sm:py-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-500/20 rounded flex items-center justify-center">
                      <Gem className="w-2 h-2 sm:w-3 sm:h-3 text-purple-400" />
                    </div>
                    <span className="text-[10px] sm:text-sm text-purple-400">Buku Agent (PRO Only)</span>
                  </div>
                  <div className="flex items-center gap-1 px-1.5 sm:px-3 py-1 sm:py-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-500/20 rounded flex items-center justify-center">
                      <Gem className="w-2 h-2 sm:w-3 sm:h-3 text-purple-400" />
                    </div>
                    <span className="text-[10px] sm:text-sm text-purple-400">Novel Agent (PRO Only)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12 mb-6 mx-0">
          <div className="text-center mb-8 px-2">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 sm:mb-3">Pertanyaan Umum</h3>
            <p className="text-xs sm:text-sm md:text-base text-gray-400 px-2">Informasi penting tentang paket berlangganan</p>
          </div>
          
          <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto px-2 sm:px-4">
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg sm:rounded-xl p-4 sm:p-6">
              <h4 className="font-semibold text-white mb-2 sm:mb-3 text-sm sm:text-base">Berapa lama paket berlaku?</h4>
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">Setiap paket berlaku selama 30 hari sejak diaktifkan setelah pembayaran dikonfirmasi.</p>
            </div>
            
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg sm:rounded-xl p-4 sm:p-6">
              <h4 className="font-semibold text-white mb-2 sm:mb-3 text-sm sm:text-base">Bagaimana cara pembayaran?</h4>
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">Pembayaran melalui transfer bank. Konfirmasi diproses maksimal dalam 1x24 jam.</p>
            </div>
            
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg sm:rounded-xl p-4 sm:p-6">
              <h4 className="font-semibold text-white mb-2 sm:mb-3 text-sm sm:text-base">Apakah kredit bisa disimpan?</h4>
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">Kredit tidak dapat dipindahkan dan akan hangus setelah masa berlaku habis.</p>
            </div>
            
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg sm:rounded-xl p-4 sm:p-6">
              <h4 className="font-semibold text-white mb-2 sm:mb-3 text-sm sm:text-base">Kapan paket diaktifkan?</h4>
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">Paket diaktifkan manual oleh admin setelah pembayaran dikonfirmasi.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionPackages